import {
  backupKeys,
  generateMPCKeys,
  generateWalletAddress,
  initializeSDK,
  register,
  setupSDK,
  getUser,
  checkKeyStatus,
  recoverKeys,
  requestJoinExistingWallet,
  approveJoinWalletRequest,
  getUserAssets,
} from "./fireblocks-ncw-client";

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 2000;

const fireblocksEnvironment = process.env.REACT_APP_FIREBLOCKS_ENV;
const aptosEnvironment = process.env.REACT_APP_APTOS_NETWORK;
const authProvider = process.env.REACT_APP_AUTH_PROVIDER;
const partner = process.env.REACT_APP_PARTNER;
const brandSource = process.env.REACT_APP_BRAND_SOURCE;
const idm_tx_ref = process.env.REACT_APP_IDM_TX_REF;
const api_version = process.env.REACT_APP_API_VERSION;
const bypass_vpa = process.env.REACT_APP_X_IDM_BYPASS_VPPA;
const clientId = process.env.REACT_APP_CLIENT_ID;

class Web3DevHubService {
  constructor(deviceId, accessToken) {
    if (
      !fireblocksEnvironment ||
      !aptosEnvironment ||
      !authProvider ||
      !partner ||
      !brandSource ||
      !idm_tx_ref ||
      !api_version ||
      !bypass_vpa ||
      !clientId
    ) {
      throw new Error("Missing needed env variables!");
    }

    this.user = null;
    this.sdk = null;
    this.operationStatus = {
      prepped: false,
      initialized: false,
      keysGenerated: false,
      backupCreated: false,
      walletGenerated: false,
    };
    this.retryCount = {
      generateKeys: 0,
    };

    this.config = {
      deviceId: deviceId,
      fireblocksEnvironment: fireblocksEnvironment,
      aptosEnvironment: aptosEnvironment,
      authProvider: authProvider,
      partner: partner,
      brandSource: brandSource,
      accessToken: accessToken,
      idm_tx_ref: idm_tx_ref,
      "api-version": api_version,
      "x-idm-bypass-vppa": Boolean(bypass_vpa),
      client_id: clientId,
    };

    (async()=>{this.prepSDK()})()
  }

  async prepSDK() {
    if (this.config.deviceId === null || this.config.accessToken === null) {
      throw new Error("DeviceId and/or AccessToken not set!");
    }
    setupSDK(this.config);
    this.operationStatus.prepped = true;
    await this.fetchUser()
    return true;
  }

  async initialize() {
    try {
      if (!this.operationStatus.prepped) {
        this.prepSDK();
      }
      await register();
      await initializeSDK();
      this.operationStatus.initialized = true;
      return true;
    } catch (error) {
      throw new Error("An error occured: ", error.message);
    }
  }

  async fetchUser() {
    try {
      const user = await getUser();
      this.user = user;
      return user;
    } catch (error) {
      console.log("Error fetching user: ", error.message);
      return this.user;
    }
  }

  async fetchUserAssets() {
    try {
      const userAssets = await getUserAssets();
      return userAssets;
    } catch (error) {
      console.log("Error fetching user assets: ", error.message);
      return [];
    }
  }

  async checkKeys() {
    if (!this.operationStatus.initialized) {
      await this.initialize();
    }
    try {
      const checkResult = await checkKeyStatus();
      // console.log("Check Keys Result: ", checkResult);
      // console.log(Object.keys(checkResult))
      if (Object.keys(checkResult).length === 0) {
        console.log("Empty key object returned, attempting recovery process.");
        const recoveryResult = await recoverKeys();
        if (recoveryResult) {
          const secondCheckResult = await checkKeyStatus();
          if (secondCheckResult.MPC_CMP_ECDSA_SECP256K1.keyStatus === "READY") {
            this.operationStatus.keysGenerated = true;
            return true;
          }
        }
      }
      if (Object.keys(checkResult).length > 0) {
        const keyStatus = checkResult.MPC_CMP_ECDSA_SECP256K1.keyStatus;
        if (keyStatus === "READY") {
          this.operationStatus.keysGenerated = true;
          return true;
        }
      }
    } catch (error) {
      console.log(`Error checking key status: ${error.message}`);
    }
    return false;
  }

  async checkWallet() {
    if (!this.operationStatus.initialized) {
      await this.initialize();
    }
    if (!this.user) {
      this.user = await getUser();
    }
    return this.user.address.length > 0;
  }

  async genKeys() {
    if (!this.operationStatus.initialized) {
      await this.initialize();
    }

    try {
      console.log(`Generating MPC Keys.`);
      await generateMPCKeys();
      const success = await this.checkKeys();
      if (success) {
        console.log("Backing up keys right away to ensure correct creation");
        await backupKeys();
      } else {
        throw new Error("Key creation unsuccessful!");
      }
    } catch (error) {
      console.error(`MPC Key Generation Error: ${error.message}`);

      if (this.retryCount.generateKeys < MAX_RETRIES) {
        const delay =
          INITIAL_RETRY_DELAY * Math.pow(2, this.retryCount.generateKeys);
        this.retryCount.generateKeys++;

        console.log(
          `Retrying MPC Key Generation in ${delay}ms (Attempt ${this.retryCount.generateKeys} of ${MAX_RETRIES})`
        );

        await this.sleep(delay);
        return await this.genKeys();
      } else {
        throw new Error(
          `Failed to generate MPC keys after ${MAX_RETRIES} attempts: ${error.message}`
        );
      }
    }
    this.operationStatus.keysGenerated = true;

    return true;
  }

  async generateWallet() {
    if (!this.operationStatus.initialized) {
      await this.initialize();
    }

    try {
      await generateWalletAddress();
      this.operationStatus.walletGenerated = true;
      return true;
    } catch (error) {
      console.error(`An error occured: ${error.message}`);
      return false;
    }
  }

  async createWalletFlow() {
    try {
      if (!this.operationStatus.initialized) {
        await this.initialize();
      }
      await this.genKeys();

      await this.generateWallet();
      return true;
    } catch (error) {
      console.error(`An error occured: ${error.message}`);
      return false;
    }
  }

  async joinExistingWalletFlow() {
    try {
      await requestJoinExistingWallet();
      const requestId = window.lastRequestId;

      if (requestId) {
        await approveJoinWalletRequest(requestId);

        return true;
      }
    } catch (error) {
      console.log("An error occured joining existing wallet: ", error.message);
    }
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export default Web3DevHubService;
